import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login, config } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#0f172a] border border-[#1e293b] rounded-sm p-8"
        data-testid="login-form"
      >
        <div className="text-[10px] tracking-[0.3em] uppercase font-mono text-blue-400 mb-2">
          sign_in
        </div>
        <h1 className="text-2xl font-semibold text-slate-50 mb-8">
          Welcome back<span className="cursor-blink" />
        </h1>

        <div className="space-y-5">
          <div>
            <Label htmlFor="email" className="text-[10px] tracking-[0.2em] uppercase font-mono text-slate-400">
              Email
            </Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 bg-[#020617] border-[#1e293b] rounded-sm font-mono text-sm"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-[10px] tracking-[0.2em] uppercase font-mono text-slate-400">
              Password
            </Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 bg-[#020617] border-[#1e293b] rounded-sm font-mono text-sm"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full rounded-sm bg-blue-500 hover:bg-blue-400 text-white"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
            Sign in
          </Button>
        </div>

        <div className="mt-6 pt-6 border-t border-[#1e293b] text-xs font-mono text-slate-500 flex items-center justify-between">
          <Link to="/register" className="text-blue-400 hover:text-blue-300" data-testid="login-goto-register">
            → create account
          </Link>
          <span className="text-slate-600">mode: {config?.mode || "email"}</span>
        </div>
      </form>
    </div>
  );
}
